import { cn, useCopyToClipboard, useDetailsPane, useIsTextTruncated } from "@istari-frontend/shared";
import { CheckIcon, ChevronRight, CopyIcon, XIcon } from "lucide-react";
import { Collapsible as CollapsiblePrimitive, Slot as SlotPrimitive } from "radix-ui";
import * as React from "react";
import { useRef } from "react";

import { Button } from "./button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { TruncatedText } from "./truncated-text";

export function DetailsPaneHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="details-pane-header"
      className={cn("flex shrink-0 items-center gap-2 border-b px-4 py-3", className)}
      {...props}
    />
  );
}

export function DetailsPaneTitle({ className, ...props }: React.ComponentProps<"h3">) {
  return (
    <h3
      data-slot="details-pane-title"
      className={cn("text-foreground min-w-0 flex-1 truncate text-[0.8125rem] font-medium", className)}
      {...props}
    />
  );
}

export function DetailsPaneDescription({ className, ...props }: React.ComponentProps<"p">) {
  return <p data-slot="details-pane-description" className={cn("sr-only", className)} {...props} />;
}

export function DetailsPaneClose({ className, ...props }: React.ComponentProps<typeof Button>) {
  const { setIsOpen, isMobile } = useDetailsPane();

  if (isMobile) {
    return null;
  }

  return (
    <Button
      data-slot="details-pane-close"
      variant="ghost"
      size="icon-2xs"
      className={cn("shrink-0", className)}
      onClick={() => setIsOpen(false)}
      aria-label="Close details pane"
      {...props}
    >
      <XIcon />
    </Button>
  );
}

export function DetailsPaneContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="details-pane-content" className={cn("flex-1 overflow-y-auto", className)} {...props} />;
}

export function DetailsPanePropertyList({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="details-pane-property-list" className={cn("space-y-1 px-4 py-2", className)} {...props} />;
}

interface DetailsPanePropertyProps extends React.ComponentProps<"div"> {
  icon?: React.ReactNode;
  label: string;
}

// The label column gives way before the value does: a details pane can be dragged down to a width
// that fits neither, and a truncated label is still readable from its position while a truncated
// value is just a first character.
export function DetailsPaneProperty({ icon, label, children, className, ...props }: DetailsPanePropertyProps) {
  return (
    <div
      data-slot="details-pane-property"
      className={cn("grid h-7.5 grid-cols-[minmax(0,min(7rem,40%))_minmax(0,1fr)] items-center gap-x-4", className)}
      {...props}
    >
      <div data-slot="details-pane-property-label" className="text-muted-foreground truncate text-[0.75rem]">
        {label}
      </div>
      <div
        data-slot="details-pane-property-value"
        className="text-foreground flex min-w-0 items-center gap-2 overflow-visible text-[0.75rem]"
      >
        {icon && (
          <div data-slot="details-pane-property-icon" className="text-muted-foreground shrink-0 [&>svg]:size-4">
            {icon}
          </div>
        )}
        <div
          data-slot="details-pane-property-children"
          className="w-full min-w-0 flex-1 overflow-visible *:data-[details-pane-interactive=true]:-ml-1.5"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function DetailsPaneSection({
  defaultOpen = true,
  className,
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.Root>) {
  return (
    <CollapsiblePrimitive.Root
      data-slot="details-pane-section"
      defaultOpen={defaultOpen}
      className={cn("pb-3", className)}
      {...props}
    />
  );
}

interface DetailsPaneSectionTriggerProps extends Omit<
  React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleTrigger>,
  "asChild"
> {
  count?: number;
  action?: React.ReactNode;
  asChild?: boolean;
}

export function DetailsPaneSectionTrigger({
  children,
  count,
  action,
  className,
  asChild = false,
  ...props
}: DetailsPaneSectionTriggerProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "span";

  return (
    <div
      data-slot="details-pane-section-trigger-wrapper"
      className="bg-sidebar border-sidebar-border flex h-8 shrink-0 items-center gap-1.5 border-l px-0.5 shadow-xs"
    >
      <CollapsiblePrimitive.CollapsibleTrigger
        data-slot="details-pane-section-trigger"
        className={cn(
          "group flex flex-1 items-center gap-1.5 text-xs outline-none",
          "focus-visible:ring-ring rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2",
          className,
        )}
        {...props}
      >
        <ChevronRight
          data-slot="details-pane-section-chevron"
          aria-hidden
          className="text-foreground size-4 shrink-0 transition-transform duration-200 ease-out group-data-[state=open]:rotate-90 motion-reduce:transition-none"
        />
        <Comp className="text-muted-foreground text-left text-xs font-bold">{children}</Comp>
        <span aria-hidden="true" className="flex-1" />
        {count !== undefined && (
          <span data-slot="details-pane-section-count" className="text-muted-foreground text-xs tabular-nums">
            {count}
          </span>
        )}
      </CollapsiblePrimitive.CollapsibleTrigger>
      {action && (
        <div data-slot="details-pane-section-action" className="shrink-0 pr-1">
          {action}
        </div>
      )}
    </div>
  );
}

export function DetailsPaneSectionContent({
  className,
  ...props
}: React.ComponentProps<typeof CollapsiblePrimitive.CollapsibleContent>) {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      data-slot="details-pane-section-content"
      className={cn(
        "bg-background border-sidebar-border overflow-hidden border-l",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:slide-out-to-top-1 data-[state=open]:slide-in-from-top-1",
        className,
      )}
      {...props}
    />
  );
}

interface DetailsPaneItemProps extends React.ComponentProps<"div"> {
  asChild?: boolean;
}

export function DetailsPaneItem({ children, className, asChild = false, ...props }: DetailsPaneItemProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "div";

  return (
    <Comp
      data-slot="details-pane-item"
      className={cn(
        "flex items-center gap-3 px-4 py-2",
        "hover:bg-accent/50 transition-colors duration-200 ease-out",
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

export function DetailsPaneItemIcon({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="details-pane-item-icon"
      className={cn("text-muted-foreground shrink-0 [&>svg]:size-4", className)}
      {...props}
    />
  );
}

export function DetailsPaneItemContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="details-pane-item-content" className={cn("min-w-0 flex-1", className)} {...props} />;
}

export function DetailsPaneItemAction({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="details-pane-item-action" className={cn("shrink-0", className)} {...props} />;
}

export function DetailsPaneSeparator({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="details-pane-separator" className={cn("bg-border mx-4 my-2 h-px", className)} {...props} />;
}

export function DetailsPaneEmptyState({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="details-pane-empty-state"
      className={cn("flex h-full flex-col items-center justify-center p-4 text-center", className)}
      {...props}
    />
  );
}

interface DetailsPanePropertyCopyButtonProps {
  value: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  truncate?: boolean;
  darkerHoverBackground?: boolean;
  allowOverflow?: boolean;
}

export function DetailsPanePropertyCopyButton({
  value,
  icon,
  children,
  truncate = true,
  darkerHoverBackground = false,
  allowOverflow = false,
}: DetailsPanePropertyCopyButtonProps) {
  const { copied, copy } = useCopyToClipboard({ timeout: 2000 });
  const shouldTruncate = truncate && !allowOverflow;
  const fadedChildren = (
    <span className={cn(copied ? "animate-blur-fade-out pointer-events-none" : "animate-blur-fade-in")}>
      {children}
    </span>
  );

  return (
    <button
      type="button"
      data-slot="details-pane-property-copy-button"
      data-details-pane-interactive="true"
      className={cn(
        "group/copy flex w-full max-w-full min-w-0 items-center gap-2 rounded-sm bg-transparent px-1.5 py-0.5 text-left",
        allowOverflow ? "overflow-visible" : "overflow-hidden",
        darkerHoverBackground
          ? "hover:bg-foreground/8 hover:text-foreground"
          : "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        darkerHoverBackground
          ? "focus-visible:bg-foreground/8 focus-visible:text-foreground"
          : "focus-visible:bg-accent focus-visible:text-accent-foreground dark:focus-visible:bg-accent/50",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        "transition-[color] duration-200 ease-out",
      )}
      onClick={(e) => {
        e.stopPropagation();
        copy(value);
      }}
      aria-live="polite"
    >
      {icon && (
        <div className="text-muted-foreground relative size-4 shrink-0">
          <div
            className={cn(
              "ease transition-[opacity,filter,transform] duration-150 [&>svg]:size-4",
              copied
                ? "scale-98 opacity-0 blur-[2px]"
                : "group-hover/copy:scale-98 group-hover/copy:opacity-0 group-hover/copy:blur-[2px] group-focus-visible/copy:scale-98 group-focus-visible/copy:opacity-0 group-focus-visible/copy:blur-[2px]",
            )}
          >
            {icon}
          </div>
          <CopyIcon
            className={cn(
              "ease absolute inset-0 size-4 transition-[opacity,filter,transform] delay-[20ms] duration-200",
              copied
                ? "scale-98 opacity-0 blur-[2px]"
                : "scale-98 opacity-0 blur-[2px] group-hover/copy:scale-100 group-hover/copy:opacity-100 group-hover/copy:blur-none group-focus-visible/copy:scale-100 group-focus-visible/copy:opacity-100 group-focus-visible/copy:blur-none",
            )}
          />
          <CheckIcon
            className={cn(
              "absolute inset-0 size-4 text-green-600 transition-[opacity,filter,transform] duration-200 ease-out dark:text-green-400",
              copied ? "scale-100 opacity-100 blur-none delay-[20ms]" : "scale-98 opacity-0 blur-[2px]",
            )}
          />
        </div>
      )}
      <span className="relative block min-w-0 flex-1">
        {shouldTruncate ? (
          <TruncatedText tooltipText={value}>{fadedChildren}</TruncatedText>
        ) : (
          <span className="block whitespace-nowrap">{fadedChildren}</span>
        )}
        {copied && (
          <span
            className={cn("animate-blur-fade-in absolute inset-0", shouldTruncate ? "truncate" : "whitespace-nowrap")}
          >
            Copied
          </span>
        )}
      </span>
    </button>
  );
}

// Avatar rings default to ring-background. On hover this trigger paints bg-accent, so the
// background-colored ring leaves a faint halo; match ring to the hover background instead.
export const detailsPanePropertyTriggerButtonStyles = cn(
  "group/trigger flex w-full max-w-full min-w-0 items-center gap-2 rounded-sm bg-transparent px-1.5 py-0.5 text-left",
  "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
  "focus-visible:bg-accent focus-visible:text-accent-foreground dark:focus-visible:bg-accent/50",
  "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
  "hover:[&_[data-slot=avatar]]:ring-accent focus-visible:[&_[data-slot=avatar]]:ring-accent",
  "dark:hover:[&_[data-slot=avatar]]:ring-accent/50 dark:focus-visible:[&_[data-slot=avatar]]:ring-accent/50",
  "transition-[color] duration-200 ease-out",
);

interface DetailsPanePropertyTriggerButtonProps extends React.ComponentProps<"button"> {
  icon?: React.ReactNode;
  actionIcon?: React.ReactNode;
  tooltipText?: string;
}

export function DetailsPanePropertyTriggerButton({
  icon,
  actionIcon,
  children,
  className,
  tooltipText,
  ...props
}: DetailsPanePropertyTriggerButtonProps) {
  const contentRef = useRef<HTMLSpanElement>(null);
  const isTruncated = useIsTextTruncated(contentRef, tooltipText, Boolean(tooltipText));

  const button = (
    <button
      type="button"
      data-slot="details-pane-property-trigger-button"
      data-details-pane-interactive="true"
      className={cn(detailsPanePropertyTriggerButtonStyles, className)}
      {...props}
    >
      {icon && (
        <div className="text-muted-foreground relative size-4 shrink-0">
          <div
            className={cn(
              "ease transition-[opacity,filter,transform] duration-150 [&>svg]:size-4",
              actionIcon &&
                "group-hover/trigger:scale-98 group-hover/trigger:opacity-0 group-hover/trigger:blur-[2px] group-focus-visible/trigger:scale-98 group-focus-visible/trigger:opacity-0 group-focus-visible/trigger:blur-[2px]",
            )}
          >
            {icon}
          </div>
          {actionIcon && (
            <div
              className={cn(
                "absolute inset-0 [&>svg]:size-4",
                "ease scale-98 opacity-0 blur-[2px] transition-[opacity,filter,transform] delay-[20ms] duration-200",
                "group-hover/trigger:scale-100 group-hover/trigger:opacity-100 group-hover/trigger:blur-none",
                "group-focus-visible/trigger:scale-100 group-focus-visible/trigger:opacity-100 group-focus-visible/trigger:blur-none",
              )}
            >
              {actionIcon}
            </div>
          )}
        </div>
      )}
      <span ref={contentRef} className="block min-w-0 flex-1 truncate">
        {children}
      </span>
    </button>
  );

  if (tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        {isTruncated ? <TooltipContent>{tooltipText}</TooltipContent> : null}
      </Tooltip>
    );
  }

  return button;
}
